import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { splitDuration, parseDuration } from "@/lib/format";

const inputCls =
  "bg-[#121212] border-[#2D2D2D] text-white placeholder:text-zinc-600 focus-visible:ring-[#00F0FF] focus-visible:ring-2";

export default function SongForm({ initial, onSubmit, onCancel }) {
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [songKey, setSongKey] = useState("");
  const [bpm, setBpm] = useState("");
  const [min, setMin] = useState("");
  const [sec, setSec] = useState("");
  const [equipment, setEquipment] = useState("");
  const [lyrics, setLyrics] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (initial) {
      setTitle(initial.title || "");
      setArtist(initial.artist || "");
      setSongKey(initial.key || "");
      setBpm(initial.bpm ? String(initial.bpm) : "");
      const d = splitDuration(initial.duration_seconds);
      setMin(String(d.minutes));
      setSec(String(d.seconds));
      setEquipment(initial.equipment || "");
      setLyrics(initial.lyrics || "");
    }
  }, [initial]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim() || !artist.trim()) {
      setError("Título e artista são obrigatórios.");
      return;
    }
    setError("");
    onSubmit({
      title: title.trim(),
      artist: artist.trim(),
      key: songKey.trim(),
      bpm: parseInt(bpm || "0", 10) || 0,
      duration_seconds: parseDuration(min, sec),
      equipment: equipment.trim(),
      lyrics: lyrics.trim(),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 mt-2" data-testid="song-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="title" className="text-zinc-300">Título *</Label>
          <Input
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex.: Black Dog"
            className={inputCls}
            data-testid="song-title-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="artist" className="text-zinc-300">Artista / Banda *</Label>
          <Input
            id="artist"
            value={artist}
            onChange={(e) => setArtist(e.target.value)}
            placeholder="Ex.: Led Zeppelin"
            className={inputCls}
            data-testid="song-artist-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="space-y-2 col-span-2 sm:col-span-1">
          <Label htmlFor="key" className="text-zinc-300">Tom</Label>
          <Input
            id="key"
            value={songKey}
            onChange={(e) => setSongKey(e.target.value)}
            placeholder="Sol Maior"
            className={`${inputCls} font-mono`}
            data-testid="song-key-input"
          />
        </div>
        <div className="space-y-2 col-span-2 sm:col-span-1">
          <Label htmlFor="bpm" className="text-zinc-300">BPM</Label>
          <Input
            id="bpm"
            type="number"
            min="0"
            value={bpm}
            onChange={(e) => setBpm(e.target.value)}
            placeholder="120"
            className={`${inputCls} font-mono`}
            data-testid="song-bpm-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="min" className="text-zinc-300">Min</Label>
          <Input
            id="min"
            type="number"
            min="0"
            value={min}
            onChange={(e) => setMin(e.target.value)}
            placeholder="3"
            className={`${inputCls} font-mono`}
            data-testid="song-min-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="sec" className="text-zinc-300">Seg</Label>
          <Input
            id="sec"
            type="number"
            min="0"
            max="59"
            value={sec}
            onChange={(e) => setSec(e.target.value)}
            placeholder="45"
            className={`${inputCls} font-mono`}
            data-testid="song-sec-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="equipment" className="text-zinc-300">
          Configurações de Equipamento
        </Label>
        <Textarea
          id="equipment"
          value={equipment}
          onChange={(e) => setEquipment(e.target.value)}
          placeholder="Pedais, captadores, presets do amp, ajustes específicos..."
          rows={3}
          className={inputCls}
          data-testid="song-equipment-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="lyrics" className="text-zinc-300">
          Notas / Letra
        </Label>
        <Textarea
          id="lyrics"
          value={lyrics}
          onChange={(e) => setLyrics(e.target.value)}
          placeholder="Letra, cifra ou anotações para tocar no palco..."
          rows={5}
          className={inputCls}
          data-testid="song-lyrics-input"
        />
      </div>

      {error && (
        <p className="text-sm text-red-400 font-medium" data-testid="form-error">
          {error}
        </p>
      )}

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="bg-transparent border-[#2D2D2D] text-white hover:bg-[#2D2D2D]"
          data-testid="song-form-cancel"
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          className="bg-[#00F0FF] hover:bg-[#33F3FF] text-black font-semibold"
          data-testid="song-form-submit"
        >
          {initial ? "Salvar alterações" : "Cadastrar"}
        </Button>
      </div>
    </form>
  );
}
